import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchFeedbackList,
  fetchFeedbackDetail,
  fetchFeedbackUnreadCount,
  sendAdminReply,
  updateConversationStatus,
  updateConversationPriority,
  markAdminRead,
  type FeedbackListParams,
} from '@/lib/api/admin-feedback'
import type { ConversationStatus, ConversationPriority } from '@hangarmax/types'

export const feedbackKeys = {
  all: ['admin-feedback'] as const,
  list: (params: FeedbackListParams) => [...feedbackKeys.all, 'list', params] as const,
  detail: (id: string) => [...feedbackKeys.all, 'detail', id] as const,
  unreadCount: () => [...feedbackKeys.all, 'unread-count'] as const,
}

export function useAdminFeedbackList(params: FeedbackListParams) {
  return useQuery({
    queryKey: feedbackKeys.list(params),
    queryFn: () => fetchFeedbackList(params),
    refetchInterval: 30_000,
  })
}

export function useAdminFeedbackUnreadCount() {
  return useQuery({
    queryKey: feedbackKeys.unreadCount(),
    queryFn: fetchFeedbackUnreadCount,
    refetchInterval: 30_000,
    select: (data) => data.count,
  })
}

export function useAdminFeedbackDetail(conversationId: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.detail(conversationId ?? ''),
    queryFn: () => fetchFeedbackDetail(conversationId!),
    enabled: !!conversationId,
  })
}

export function useAdminReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { conversationId: string; body: string }) =>
      sendAdminReply(vars.conversationId, vars.body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedbackKeys.detail(vars.conversationId) })
      qc.invalidateQueries({ queryKey: feedbackKeys.all })
      toast.success('Reply sent')
    },
    onError: (err) => toast.error(`Failed to send reply: ${err.message}`),
  })
}

export function useUpdateConversationStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { conversationId: string; status: ConversationStatus; notifyUser?: boolean }) =>
      updateConversationStatus(vars.conversationId, vars.status, vars.notifyUser),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedbackKeys.detail(vars.conversationId) })
      qc.invalidateQueries({ queryKey: feedbackKeys.all })
      toast.success(vars.notifyUser ? 'Status updated — user notified' : 'Status updated')
    },
    onError: (err) => toast.error(`Failed to update status: ${err.message}`),
  })
}

export function useUpdateConversationPriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { conversationId: string; priority: ConversationPriority }) =>
      updateConversationPriority(vars.conversationId, vars.priority),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedbackKeys.detail(vars.conversationId) })
      qc.invalidateQueries({ queryKey: feedbackKeys.all })
      toast.success('Priority updated')
    },
    onError: (err) => toast.error(`Failed to update priority: ${err.message}`),
  })
}

export function useMarkAdminRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: markAdminRead,
    onSuccess: (_data, conversationId) => {
      qc.invalidateQueries({ queryKey: feedbackKeys.detail(conversationId) })
      qc.invalidateQueries({ queryKey: feedbackKeys.all })
    },
  })
}
